import { ref, type Ref } from 'vue';
import type { FeatureGateName } from '../index';
import { getFeatureFlagProvider, onFeatureFlagProviderChange } from './registry';
import { getOverride, onOverridesChange } from './override-store';
import { isDevMode } from '../parcels/utils/is-dev-mode';
import { getCachedCatalog } from './use-feature-flag-catalog';
import { markGateUsed } from './used-gates';

import { onScopeDisposeOrWarn } from '@platform/vue/on-scope-dispose-or-warn';

// Dedupe: warn at most once per unknown id across the process lifetime.
const warnedUnknownIds = new Set<string>();

/**
 * Dev-only: warn once when `id` is absent from a loaded, non-empty catalog (likely a typo).
 * Stays silent when the catalog is empty/not-yet-loaded to avoid false positives. Reads the
 * cached catalog synchronously — never triggers a fetch.
 */
function warnOnUnknownGate(id: string): void {
  if (!isDevMode()) return;
  const catalog = getCachedCatalog();
  if (catalog.length === 0) return;
  if (catalog.some((item) => item.id === id)) return;
  if (warnedUnknownIds.has(id)) return;
  warnedUnknownIds.add(id);
  console.warn(
    `[feature-flags] Unknown gate id "${id}" — not found in the provider catalog. ` +
      'Check for a typo, or the gate may not be registered.',
  );
}

export interface UseFeatureGateOptions {
  /** Value used when no provider is registered. Defaults to `false`. */
  default?: boolean;
}

export interface UseFeatureGateResult {
  /** Reactive gate state. Re-resolves on provider value updates. */
  isEnabled: Ref<boolean>;
  /** True while an async provider resolution is in flight. */
  isLoading: Ref<boolean>;
}

/**
 * Resolve a feature gate to a reactive boolean.
 *
 * Precedence:
 *   1. A dev override (dev-only local store) WINS over everything else.
 *   2. Otherwise the registered {@link FeatureFlagProvider} answers via `checkGate`
 *      (synchronously through `checkGateSync` when available, else awaited).
 *   3. With no provider registered, falls back to `opts.default ?? false`.
 *
 * Reactive: re-resolves when the provider signals a value update via `onValuesUpdate`.
 * A generation counter discards stale async results so a slow earlier resolution can
 * never overwrite a newer one.
 */
export function useFeatureGate(
  id: FeatureGateName,
  opts: UseFeatureGateOptions = {},
): UseFeatureGateResult {
  const fallback = opts.default ?? false;
  const isEnabled = ref(fallback);
  const isLoading = ref(false);

  // Record that this gate is in use by the loaded app so the DevTools panel can
  // emphasise it. Idempotent and side-effect-light (a window-anchored set).
  markGateUsed(id);

  // Dev-only typo guard. Does not touch resolution — purely diagnostic.
  warnOnUnknownGate(id);

  // Discards out-of-order async resolutions when a newer resolve has started.
  let generation = 0;

  function resolve(): void {
    const token = (generation += 1);

    // 1. Dev override wins — synchronous, no provider involved.
    const override = getOverride(id);
    if (override !== undefined) {
      isEnabled.value = override;
      isLoading.value = false;
      return;
    }

    const provider = getFeatureFlagProvider();

    // 3. No provider — use the caller's default.
    if (!provider) {
      isEnabled.value = fallback;
      isLoading.value = false;
      return;
    }

    // 2a. Synchronous provider read — no loading flash.
    if (provider.checkGateSync) {
      isEnabled.value = provider.checkGateSync(id);
      isLoading.value = false;
      return;
    }

    // 2b. Possibly-async provider read.
    const result = provider.checkGate(id);
    if (typeof result === 'boolean') {
      isEnabled.value = result;
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    result
      .then((value) => {
        if (token !== generation) return;
        isEnabled.value = value;
        isLoading.value = false;
      })
      .catch(() => {
        if (token !== generation) return;
        isEnabled.value = fallback;
        isLoading.value = false;
      });
  }

  resolve();

  // Subscribe to the CURRENT provider's value channel, re-establishing the
  // subscription whenever the provider itself is replaced. A provider may be
  // registered after the hook first runs (async SDK init) or swapped at
  // runtime; without re-subscribing, the hook stays bound to a stale provider
  // and never reflects the new one. `onFeatureFlagProviderChange` also fires
  // `resolve()` so the value re-reads against whichever provider is now active.
  let unsubscribeValues: (() => void) | undefined;
  function subscribeToProviderValues(): void {
    unsubscribeValues?.();
    unsubscribeValues = getFeatureFlagProvider()?.onValuesUpdate?.(resolve);
  }
  subscribeToProviderValues();

  const unsubscribeProviderChange = onFeatureFlagProviderChange(() => {
    resolve();
    subscribeToProviderValues();
  });

  // Dev overrides re-resolve live: without this, a DevTools flip only lands
  // on the next full page load (the provider's update channel never fires
  // for local override writes).
  const unsubscribeOverrides = onOverridesChange(resolve);

  onScopeDisposeOrWarn(() => {
    unsubscribeValues?.();
    unsubscribeProviderChange();
    unsubscribeOverrides();
  }, 'useFeatureGate()');

  return { isEnabled, isLoading };
}
